import React, { useEffect, useState } from "react";
import { Grid, Typography, Paper } from "@mui/material";
import { getPayInData } from "./api";
import { toast } from "react-toastify";
import PayInBreadcrumb from "./PayInBreadcrumb";
import PayInFilters from "./PayInFilters";
import PayInTable from "./PayInTable";
import PayInPagination from "./PayInPagination";
import PayInDownloadButtons from "./PayInDownloadButtons";
import LoadingSpinner from "./LoadingSpinner";

const NewPayIn = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(""); // Store API response message
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Fetch data from API
    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,  // Fetch data for the updated page
                size: pageSize,     // Adjust the page size
                fromDate: fromDate || null,  // Only pass the fromDate if it's not empty
                toDate: toDate || null,      // Only pass the toDate if it's not empty
                searchQuery,
            };
            const response = await getPayInData(params);
            console.log("API Response:", response?.data?.body?.data);

            if (response?.status === 204 || !response?.data?.body?.data) {
                console.log("No transactions found");
                toast.warn("No transactions found (204)");
                setMessage("No transactions found");
                setData([]);
                setFilteredData([]);
            } else {
                const newData = response?.data?.body?.data || [];
                setData(newData);
                setFilteredData(newData);
                setTotalRecords(response.data.body.totalRecords || 0);  // Assuming totalRecords is provided in the response
                setMessage("");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    // Use Effect to call fetchData when necessary (pagination or filters change)
    useEffect(() => {
        fetchData(); // Call fetchData on change of pagination or filters
    }, [currentPage, pageSize, searchQuery]);

    // Handle search filter
    const handleSearch = () => {
        setCurrentPage(0); // Reset to first page when searching
        fetchData(); // Call fetchData to fetch results with updated filters
    };

    // Handle clearing filters
    const handleClear = () => {
        console.log("Clearing filters");
        console.log("Current Page:", currentPage);
        console.log("Page Size:", pageSize);
        console.log("fromDate:", fromDate);
        console.log("toDate:", toDate);
        console.log("searchQuery:", searchQuery);
        // Reset states for the filters
        setFromDate("");
        setToDate("");
        setSearchQuery("");
        setCurrentPage(0); // Reset to first page when clearing filters
        
        // Call fetchData explicitly with page = 0 and size = 10 after clearing filters
        fetchData(); // This will call the API with currentPage = 0 and pageSize = 10
    };

    const handlePageChange = (event, value) => {
        console.log("Page change:", value);
        setCurrentPage(value - 1); // Update current page state (pagination starts from 1)
    };

    const handlePageSizeChange = (event) => {
        setPageSize(event.target.value); // Update page size state
        setCurrentPage(0); // Reset to first page when page size is changed
    };

    return (
        <Paper sx={{ padding: "1.5rem", margin: "1rem" }}>
            <Grid container direction="column" spacing={2}>
                {/* Breadcrumb */}
                <Grid item>
                    <PayInBreadcrumb />
                </Grid>

                {/* Title */}
                <Grid item sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Grid item>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            Pay In Report
                        </Typography>
                    </Grid>
                    {/* Download Buttons */}
                    <Grid item>
                        <PayInDownloadButtons filteredData={filteredData} />
                    </Grid>
                </Grid>

                {/* Filters */}
                <Grid item>
                    <PayInFilters
                        fromDate={fromDate}
                        toDate={toDate}
                        searchQuery={searchQuery}
                        setFromDate={setFromDate}
                        setToDate={setToDate}
                        setSearchQuery={setSearchQuery}
                        handleSearch={handleSearch}
                        handleClear={handleClear}
                    />
                </Grid>

                {/* Loading Spinner */}
                {loading ? (
                    <Grid item>
                        <LoadingSpinner />
                    </Grid>
                ) : (
                    <>
                        {/* Data Table */}
                        <Grid item>
                            <PayInTable filteredData={filteredData} message={message} />
                        </Grid>

                        {/* Pagination (Only if data is available) */}
                        {filteredData.length > 0 && (
                            <Grid item>
                                <PayInPagination
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    totalRecords={totalRecords}
                                    handlePageChange={handlePageChange}
                                    handlePageSizeChange={handlePageSizeChange}
                                />
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </Paper>
    );
};

export default NewPayIn;
